import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { uploadFileToS3 } from "services/aws";
import {
  submitCheckInFormApi,
  validateCheckInTokenApi,
} from "services/postApis";
import { ErrorToast, SuccessToast } from "utils/toast";
import ButtonLoader from "components/ButtonLoader";
import btnArrow from "assets/icons/btn_arrow.svg";
import logo from "assets/logo.png";
import "styles/SubscriptionCheckIn.css";

const HEALTH_CHANGE_OPTIONS = [
  "Much better",
  "Somewhat better",
  "About the same",
  "Somewhat worse",
  "Much worse",
];

const mapHealthChangeToKey = (val) => {
  switch (val) {
    case "Much better":
      return "much_better";
    case "Somewhat better":
      return "somewhat_better";
    case "About the same":
      return "about_the_same";
    case "Somewhat worse":
      return "somewhat_worse";
    case "Much worse":
      return "much_worse";
    default:
      return val ? val.toLowerCase().replace(/\s+/g, "_") : "";
  }
};

const FEELING_AREAS = [
  { key: "digestion", label: "Digestion / appetite" },
  { key: "sleep", label: "Sleep" },
  { key: "energy", label: "Energy" },
  { key: "mood", label: "Mood / stress" },
  { key: "hormonal", label: "Menstrual or hormonal symptoms" },
];

const FEELING_RESPONSES = ["Improved", "Same", "Worse", "Not relevant"];

const mapAreaRating = (val) => {
  switch (val) {
    case "Improved":
      return "improved";
    case "Same":
      return "same";
    case "Worse":
      return "worse";
    case "Not relevant":
      return "not_relevant";
    default:
      return val ? val.toLowerCase().replace(/\s+/g, "_") : "not_relevant";
  }
};

const CONSISTENCY_OPTIONS = [
  "Every day",
  "Most days",
  "Some days",
  "I have not taken it",
];

const mapConsistencyToKey = (val) => {
  switch (val) {
    case "Every day":
      return "every_day";
    case "Most days":
      return "most_days";
    case "Some days":
      return "some_days";
    case "I have not taken it":
      return "not_taken";
    default:
      return val ? val.toLowerCase().replace(/\s+/g, "_") : "";
  }
};

const NEXT_STEPS_OPTIONS = [
  "Continue the current formula",
  "Review an adjustment",
  "Discuss a refill",
  "Request a more in-depth consultation",
];

const SubscriptionCheckIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  // Token guard & validation state
  const [isValidating, setIsValidating] = useState(true);

  // Form State (No user/patient personal info fields)
  const [formData, setFormData] = useState({
    overallHealthChange: "",
    improvedSymptoms: "",
    remainingOrWorseSymptoms: "",
    mostMeaningfulChange: "",
    feelingAreas: {
      digestion: "",
      sleep: "",
      energy: "",
      mood: "",
      hormonal: "",
    },
    formulaConsistency: "",
    hasUnwantedEffects: "",
    unwantedEffectsDetails: "",
    hasFormulaChanges: "",
    formulaChangesDetails: "",
    hasHealthChanges: "",
    healthChangesDetails: "",
    nextSteps: [],
    anythingElse: "",
  });

  // Tongue photo state
  const [tonguePhoto, setTonguePhoto] = useState({
    file: null,
    previewUrl: null,
    isDragging: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Guard: Validate token from URL parameter
  useEffect(() => {
    const tokenParam = searchParams.get("token");

    // Guard 1: First check if token exists in the URL. If not available, navigate to home page immediately
    if (!tokenParam || !tokenParam.trim()) {
      navigate("/", { replace: true });
      return;
    }

    // Guard 2: Check that token is valid with the validate-token API
    let isMounted = true;
    const validateToken = async () => {
      setIsValidating(true);

      try {
        const res = await validateCheckInTokenApi(tokenParam.trim());
        if (!isMounted) return;

        if (res?.success) {
          setIsValidating(false);
        } else {
          ErrorToast(res?.message || "Invalid or expired check-in token.");
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Unable to validate check-in token.";
        ErrorToast(msg);
        navigate("/", { replace: true });
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate]);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (tonguePhoto.previewUrl) {
        URL.revokeObjectURL(tonguePhoto.previewUrl);
      }
    };
  }, [tonguePhoto.previewUrl]);

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Matrix change handler
  const handleFeelingAreaChange = (areaKey, responseValue) => {
    setFormData((prev) => ({
      ...prev,
      feelingAreas: {
        ...prev.feelingAreas,
        [areaKey]: responseValue,
      },
    }));
  };

  // Multi-select checkbox handler
  const handleNextStepToggle = (option) => {
    setFormData((prev) => {
      const exists = prev.nextSteps.includes(option);
      return {
        ...prev,
        nextSteps: exists
          ? prev.nextSteps.filter((item) => item !== option)
          : [...prev.nextSteps, option],
      };
    });
  };

  // Tongue file selection
  const handleFileSelect = (file) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type.toLowerCase())) {
      ErrorToast("Please select a valid image file (JPEG, PNG, or WebP).");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      ErrorToast("Image file size must be less than 10MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setTonguePhoto({
      file,
      previewUrl,
      isDragging: false,
    });
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    if (tonguePhoto.previewUrl) {
      URL.revokeObjectURL(tonguePhoto.previewUrl);
    }
    setTonguePhoto({
      file: null,
      previewUrl: null,
      isDragging: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Overall Health Change (Mandatory)
    if (!formData.overallHealthChange) {
      ErrorToast("Please select how your overall health has changed.");
      return;
    }

    // 2. Improved Symptoms (Mandatory)
    if (!formData.improvedSymptoms.trim()) {
      ErrorToast("Please describe which symptoms or concerns have improved.");
      return;
    }

    // 3. Remaining or Worse Symptoms (Mandatory)
    if (!formData.remainingOrWorseSymptoms.trim()) {
      ErrorToast("Please describe which symptoms remain, have returned, or feel worse.");
      return;
    }

    // 4. Most Meaningful Change (Mandatory)
    if (!formData.mostMeaningfulChange.trim()) {
      ErrorToast("Please describe what change has mattered most to you this month.");
      return;
    }

    // 5. Area Ratings (All 5 matrix areas mandatory)
    const missingAreas = FEELING_AREAS.filter(
      (area) => !formData.feelingAreas[area.key]
    );
    if (missingAreas.length > 0) {
      ErrorToast(`Please rate how your "${missingAreas[0].label}" has been.`);
      return;
    }

    // 6. Formula Consistency (Mandatory)
    if (!formData.formulaConsistency) {
      ErrorToast("Please select how consistently you have taken the formula.");
      return;
    }

    // 7. Unwanted Effects (Mandatory selection; description mandatory if Yes, empty if No)
    if (!formData.hasUnwantedEffects) {
      ErrorToast("Please select Yes or No for whether you noticed any unwanted effects.");
      return;
    }
    if (formData.hasUnwantedEffects === "Yes" && !formData.unwantedEffectsDetails.trim()) {
      ErrorToast("Please describe the unwanted effects or sensitivity you noticed.");
      return;
    }

    // 8. Formula Changes (Mandatory selection; description mandatory if Yes, empty if No)
    if (!formData.hasFormulaChanges) {
      ErrorToast("Please select Yes or No for whether you made any changes to how you take the formula.");
      return;
    }
    if (formData.hasFormulaChanges === "Yes" && !formData.formulaChangesDetails.trim()) {
      ErrorToast("Please describe the changes you made to how you take the formula.");
      return;
    }

    // 9. Health Changes (Mandatory selection; description mandatory if Yes, empty if No)
    if (!formData.hasHealthChanges) {
      ErrorToast("Please select Yes or No for whether there have been changes to your health or medications.");
      return;
    }
    if (formData.hasHealthChanges === "Yes" && !formData.healthChangesDetails.trim()) {
      ErrorToast("Please list what health changes occurred and when.");
      return;
    }

    // 10. Next Steps / Requested Help (Mandatory, at least one selected)
    if (!formData.nextSteps || formData.nextSteps.length === 0) {
      ErrorToast("Please select at least one option for what you would like help with this month.");
      return;
    }

    setIsSubmitting(true);

    try {
      let uploadedTongueFileName = null;

      // Upload tongue photo to S3 if attached
      if (tonguePhoto.file) {
        try {
          const uploadRes = await uploadFileToS3(tonguePhoto.file, "public/subscription-checkin-tongues");
          const rawPath =
            uploadRes?.key ||
            uploadRes?.url ||
            (typeof uploadRes === "string" ? uploadRes : "");
          uploadedTongueFileName = rawPath ? rawPath.split("/").pop() : null;
        } catch (uploadErr) {
          console.warn("Could not upload tongue photo to S3:", uploadErr);
          // Don't completely block check-in submission if photo upload fails
        }
      }

      const tokenParam = (searchParams.get("token") || "").trim();

      const payload = {
        token: tokenParam,
        overallHealthChange: mapHealthChangeToKey(formData.overallHealthChange),
        improvedSymptoms: formData.improvedSymptoms.trim(),
        remainingOrWorseSymptoms: formData.remainingOrWorseSymptoms.trim(),
        mostMeaningfulChange: formData.mostMeaningfulChange.trim(),
        areaRatings: {
          digestion: mapAreaRating(formData.feelingAreas.digestion),
          sleep: mapAreaRating(formData.feelingAreas.sleep),
          energy: mapAreaRating(formData.feelingAreas.energy),
          mood: mapAreaRating(formData.feelingAreas.mood),
          hormonal: mapAreaRating(formData.feelingAreas.hormonal),
        },
        formulaConsistency: mapConsistencyToKey(formData.formulaConsistency),
        hasUnwantedEffects: formData.hasUnwantedEffects === "Yes",
        unwantedEffectsDetails:
          formData.hasUnwantedEffects === "Yes"
            ? formData.unwantedEffectsDetails.trim()
            : "",
        hasFormulaChanges: formData.hasFormulaChanges === "Yes",
        formulaChangesDetails:
          formData.hasFormulaChanges === "Yes"
            ? formData.formulaChangesDetails.trim()
            : "",
        hasHealthChanges: formData.hasHealthChanges === "Yes",
        healthChangesDetails:
          formData.hasHealthChanges === "Yes"
            ? formData.healthChangesDetails.trim()
            : "",
        requestedHelp: formData.nextSteps,
        additionalNotes: formData.anythingElse.trim() || "",
        tongueImage: uploadedTongueFileName || null,
      };

      const res = await submitCheckInFormApi(payload);
      if (res?.success) {
        SuccessToast(res?.message || "Monthly check-in submitted successfully");
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        ErrorToast(res?.message || "Failed to submit check-in form. Please try again.");
      }
    } catch (err) {
      console.error("Submission error:", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit check-in form. Please try again.";
      ErrorToast(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── LOADING SPINNER VIEW (While Validating Token) ──
  if (isValidating) {
    return (
      <div className="checkin-page">
        <section className="checkin-hero-section">
          <div className="checkin-container">
            <div className="checkin-status-card">
              <div className="checkin-brand-header" style={{ justifyContent: "center", marginBottom: "8px" }}>
                <img src={logo} alt="Hua Shen Herbs" className="checkin-brand-logo" />
                <span className="checkin-brand-name">Hua Shen Herbs</span>
              </div>
              <div className="checkin-loading-spinner" />
              <h2 className="checkin-status-title">Verifying Check-In Link...</h2>
              <p className="checkin-status-desc">
                Please wait while we validate your subscription check-in session.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ── SUCCESS CONFIRMATION VIEW ──
  if (isSubmitted) {
    return (
      <div className="checkin-page">
        <section className="checkin-hero-section">
          <div className="checkin-container">
            <div className="checkin-success-card">
              <div className="checkin-success-icon-badge">✓</div>
              <h1 className="checkin-success-title">
                Check-In <em>Submitted!</em>
              </h1>
              <p className="checkin-success-desc">
                Thank you for checking in. Your practitioner will review your health updates, symptom progress, and formula response to guide your ongoing care plan.
              </p>

              <div className="checkin-success-recap">
                <div className="checkin-success-recap-row">
                  <span>Overall Health:</span>
                  <strong>{formData.overallHealthChange || "Submitted"}</strong>
                </div>
                <div className="checkin-success-recap-row">
                  <span>Tongue Photo:</span>
                  <strong>{tonguePhoto.file ? "Included" : "None provided"}</strong>
                </div>
              </div>

              <button
                type="button"
                className="checkin-submit-btn"
                onClick={() => navigate("/", { replace: true })}
              >
                <span>Return to Home</span>
                <img src={btnArrow} alt="" width="18" height="18" />
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      {/* ── Page Header / Hero ── */}
      <section className="checkin-hero-section">
        <div className="checkin-container">
          <div className="checkin-brand-header">
            <img src={logo} alt="Hua Shen Herbs" className="checkin-brand-logo" />
            <span className="checkin-brand-name">Hua Shen Herbs</span>
          </div>

          <h1 className="checkin-title">
            Monthly Subscription <em>Check-In Form</em>
          </h1>

          <p className="checkin-subtitle">
            Take a few moments to share how you're feeling and how your formula is working. Your practitioner will carefully review your answers to adjust your herbs and guide your healing progress.
          </p>
        </div>
      </section>

      {/* ── Main Form Container ── */}
      <div className="checkin-container">
        <form className="checkin-form" onSubmit={handleSubmit}>

          {/* Section 1: Your Progress */}
          <div className="checkin-section-card">
            <div className="checkin-section-header">
              <h2 className="checkin-section-title">
                <span className="checkin-section-icon">📈</span> Your progress
              </h2>
            </div>

            {/* Q1: Overall Health Change */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Overall, how has your health changed since starting this formula? <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-options-grid">
                {HEALTH_CHANGE_OPTIONS.map((opt) => {
                  const isSelected = formData.overallHealthChange === opt;
                  return (
                    <div
                      key={opt}
                      className={`checkin-option-item ${isSelected ? "selected" : ""}`}
                      onClick={() => setFormData((prev) => ({ ...prev, overallHealthChange: opt }))}
                    >
                      <div className="checkin-radio-indicator">
                        {isSelected && <div className="checkin-radio-dot" />}
                      </div>
                      <span className="checkin-option-label">{opt}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Q2: Which symptoms or concerns have improved? */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Which symptoms or concerns have improved? Please include any changes in severity or frequency. <span className="checkin-question-required">*</span>
              </label>
              <textarea
                name="improvedSymptoms"
                className="checkin-textarea"
                placeholder="Share any improvements you've noticed (e.g. less bloating, deeper sleep, fewer headaches)..."
                value={formData.improvedSymptoms}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            {/* Q3: Which symptoms remain, have returned, or feel worse? */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Which symptoms remain, have returned, or feel worse? <span className="checkin-question-required">*</span>
              </label>
              <textarea
                name="remainingOrWorseSymptoms"
                className="checkin-textarea"
                placeholder="Describe any lingering, returning, or worsening symptoms..."
                value={formData.remainingOrWorseSymptoms}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            {/* Q4: What change has mattered most to you this month? */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                What change has mattered most to you this month? <span className="checkin-question-required">*</span>
              </label>
              <textarea
                name="mostMeaningfulChange"
                className="checkin-textarea"
                placeholder="What shift or outcome made the biggest difference in your daily life?..."
                value={formData.mostMeaningfulChange}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
          </div>

          {/* Section 2: How you are feeling (Matrix) */}
          <div className="checkin-section-card">
            <div className="checkin-section-header">
              <h2 className="checkin-section-title">
                <span className="checkin-section-icon">🧘‍♀️</span> How you are feeling <span className="checkin-question-required">*</span>
              </h2>
              <p className="checkin-section-desc">
                Since your last check-in, how have these areas been? Mark one response in each row.
              </p>
            </div>

            <div className="checkin-matrix-wrapper">
              <table className="checkin-matrix-table">
                <thead>
                  <tr>
                    <th className="col-area">Area</th>
                    {FEELING_RESPONSES.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEELING_AREAS.map((area) => (
                    <tr key={area.key}>
                      <td className="checkin-matrix-row-title">{area.label}</td>
                      {FEELING_RESPONSES.map((res) => {
                        const isSelected = formData.feelingAreas[area.key] === res;
                        return (
                          <td
                            key={res}
                            className={`checkin-matrix-cell ${isSelected ? "selected" : ""}`}
                            onClick={() => handleFeelingAreaChange(area.key, res)}
                          >
                            <div className="checkin-matrix-radio-btn" role="radio" aria-checked={isSelected}>
                              {isSelected && <div className="checkin-radio-dot" />}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Your formula */}
          <div className="checkin-section-card">
            <div className="checkin-section-header">
              <h2 className="checkin-section-title">
                <span className="checkin-section-icon">🍵</span> Your formula
              </h2>
            </div>

            {/* Consistency */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                How consistently have you taken the formula as directed? <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-options-grid">
                {CONSISTENCY_OPTIONS.map((opt) => {
                  const isSelected = formData.formulaConsistency === opt;
                  return (
                    <div
                      key={opt}
                      className={`checkin-option-item ${isSelected ? "selected" : ""}`}
                      onClick={() => setFormData((prev) => ({ ...prev, formulaConsistency: opt }))}
                    >
                      <div className="checkin-radio-indicator">
                        {isSelected && <div className="checkin-radio-dot" />}
                      </div>
                      <span className="checkin-option-label">{opt}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Unwanted effects */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Have you noticed any unwanted effects or sensitivity while taking it? <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-yesno-row">
                {["No", "Yes"].map((val) => {
                  const isSelected = formData.hasUnwantedEffects === val;
                  return (
                    <div
                      key={val}
                      className={`checkin-yesno-item ${isSelected ? "selected" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          hasUnwantedEffects: val,
                          ...(val === "No" ? { unwantedEffectsDetails: "" } : {}),
                        }))
                      }
                    >
                      <div className="checkin-radio-indicator">
                        {isSelected && <div className="checkin-radio-dot" />}
                      </div>
                      <span className="checkin-option-label">
                        {val === "Yes" ? "Yes (please describe below)" : "No"}
                      </span>
                    </div>
                  );
                })}
              </div>
              {formData.hasUnwantedEffects === "Yes" && (
                <div className="checkin-subinput-container">
                  <textarea
                    name="unwantedEffectsDetails"
                    className="checkin-textarea"
                    placeholder="Please describe any unwanted effects, stomach sensitivities, or changes you observed..."
                    value={formData.unwantedEffectsDetails}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </div>
              )}
            </div>

            {/* Changes to how you take the formula */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Have you made any changes to how you take the formula? <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-yesno-row">
                {["No", "Yes"].map((val) => {
                  const isSelected = formData.hasFormulaChanges === val;
                  return (
                    <div
                      key={val}
                      className={`checkin-yesno-item ${isSelected ? "selected" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          hasFormulaChanges: val,
                          ...(val === "No" ? { formulaChangesDetails: "" } : {}),
                        }))
                      }
                    >
                      <div className="checkin-radio-indicator">
                        {isSelected && <div className="checkin-radio-dot" />}
                      </div>
                      <span className="checkin-option-label">
                        {val === "Yes" ? "Yes (please describe below)" : "No"}
                      </span>
                    </div>
                  );
                })}
              </div>
              {formData.hasFormulaChanges === "Yes" && (
                <div className="checkin-subinput-container">
                  <textarea
                    name="formulaChangesDetails"
                    className="checkin-textarea"
                    placeholder="Please describe any changes in dose, timing, temperature, or frequency..."
                    value={formData.formulaChangesDetails}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 4: Changes since your last check-in */}
          <div className="checkin-section-card">
            <div className="checkin-section-header">
              <h2 className="checkin-section-title">
                <span className="checkin-section-icon">🔄</span> Changes since your last check-in
              </h2>
            </div>

            {/* Health / Diagnosis / Medication Changes */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Have there been changes to your health, diagnoses, medications, supplements, allergies, or pregnancy status? <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-yesno-row">
                {["No", "Yes"].map((val) => {
                  const isSelected = formData.hasHealthChanges === val;
                  return (
                    <div
                      key={val}
                      className={`checkin-yesno-item ${isSelected ? "selected" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          hasHealthChanges: val,
                          ...(val === "No" ? { healthChangesDetails: "" } : {}),
                        }))
                      }
                    >
                      <div className="checkin-radio-indicator">
                        {isSelected && <div className="checkin-radio-dot" />}
                      </div>
                      <span className="checkin-option-label">
                        {val === "Yes" ? "Yes (please list what changed and when)" : "No"}
                      </span>
                    </div>
                  );
                })}
              </div>
              {formData.hasHealthChanges === "Yes" && (
                <div className="checkin-subinput-container">
                  <textarea
                    name="healthChangesDetails"
                    className="checkin-textarea"
                    placeholder="Please list what changed and when (new prescriptions, new vitamins, medical diagnoses, etc.)..."
                    value={formData.healthChangesDetails}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </div>
              )}
            </div>

            {/* Tongue Photo Upload */}
            <div className="checkin-field-group" style={{ marginTop: "12px" }}>
              <label className="checkin-question-label">
                You may upload a current tongue photo with your response if you would like to include one. <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: "14px" }}>(Optional)</span>
              </label>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />

              {!tonguePhoto.file ? (
                <div
                  className={`checkin-upload-zone ${tonguePhoto.isDragging ? "dragging" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setTonguePhoto((prev) => ({ ...prev, isDragging: true }));
                  }}
                  onDragLeave={() => setTonguePhoto((prev) => ({ ...prev, isDragging: false }))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                >
                  <div className="checkin-upload-content">
                    <span className="checkin-upload-icon">📷</span>
                    <p className="checkin-upload-prompt">
                      Click to upload or drag &amp; drop your tongue photo
                    </p>
                    <p className="checkin-upload-subtext">Supports PNG, JPG, or WEBP (Max 10MB)</p>
                  </div>
                </div>
              ) : (
                <div className="checkin-file-preview-card">
                  <div className="checkin-file-preview-left">
                    <img
                      src={tonguePhoto.previewUrl}
                      alt="Tongue preview"
                      className="checkin-file-thumb"
                    />
                    <div className="checkin-file-meta">
                      <p className="checkin-file-name">{tonguePhoto.file.name}</p>
                      <p className="checkin-file-size">
                        {(tonguePhoto.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="checkin-remove-file-btn"
                    onClick={handleRemovePhoto}
                  >
                    Remove Photo
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Section 5: Next steps */}
          <div className="checkin-section-card">
            <div className="checkin-section-header">
              <h2 className="checkin-section-title">
                <span className="checkin-section-icon">🌱</span> Next steps
              </h2>
            </div>

            {/* What would you like help with this month? */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                What would you like help with this month? Select any that apply. <span className="checkin-question-required">*</span>
              </label>
              <div className="checkin-options-stack">
                {NEXT_STEPS_OPTIONS.map((opt) => {
                  const isChecked = formData.nextSteps.includes(opt);
                  return (
                    <div
                      key={opt}
                      className={`checkin-option-item ${isChecked ? "selected" : ""}`}
                      onClick={() => handleNextStepToggle(opt)}
                    >
                      <div className="checkin-checkbox-indicator">
                        <span className="checkin-checkbox-check">✓</span>
                      </div>
                      <span className="checkin-option-label">{opt}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Anything else you would like me to know? */}
            <div className="checkin-field-group">
              <label className="checkin-question-label">
                Anything else you would like me to know? <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: "14px" }}>(Optional)</span>
              </label>
              <textarea
                name="anythingElse"
                className="checkin-textarea"
                placeholder="Any special requests, schedule changes, or personal observations you'd like to share..."
                value={formData.anythingElse}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
          </div>

          {/* Closing Note Banner */}
          <div className="checkin-closing-note">
            <span className="checkin-closing-icon">🌿</span>
            <p className="checkin-closing-text">
              Thank you for checking in. I will review your responses and follow up with recommended next steps, including whether your formula should continue or change.
            </p>
          </div>

          {/* Action Bar */}
          <div className="checkin-actions-bar">
            <button
              type="submit"
              className="checkin-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader size="sm" color="light" />
                  <span>Submitting Check-In...</span>
                </>
              ) : (
                <>
                  <span>Submit Check-In Form</span>
                  <img src={btnArrow} alt="" width="18" height="18" />
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default SubscriptionCheckIn;
